import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Photo from "./../../../assets/img/photo.png";
import Upload from "./../../../assets/img/upload.png";
import {
  isAiSelfieCaricatureHobbyPostPaymentActive,
  resolveHobbyCaricatureRouteSlug,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";
import TakePhotoModal from "../../../modal/TakePhotoModal";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { initHobbyPeopleSession, setHobbyGroupPhotoFlow } from "../../../redux/slices/uploadFlowSlice";
import {
  getCurrentCapturePersonNumber,
  allHobbyPersonsCompleted,
} from "../../../utils/hobbyPersonFlow";
import StationThemeLayout from "../../../layouts/StationThemeLayout";


const SelectMediaLibrary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const dispatch = useDispatch();
  const [photoModal, setPhotoModal] = useState(false);
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const { hobbyPeopleCount, hobbyPersonCompletedResults, hobbyGroupPhotoFlow } = useSelector(
    (s: RootState) => s.uploadFlow
  );

  // Get selected station (child) first, then fall back to legacy stationData
  const stationData = useMemo(() => {
    const selectedRaw = localStorage.getItem("selectedStation");
    const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
    const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
    return fromSelected || fromStorage;
  }, [location.state]);

  const aiSelfiePostPaymentHobby = isAiSelfieCaricatureHobbyPostPaymentActive();
  const caricatureHobbyByArt = shouldUseHobbyCaricatureFlowForStoredAiSelfieArt();
  const isHobbyCaptureFlow =
    stationData?.type === "hobby-caricature" ||
    aiSelfiePostPaymentHobby ||
    caricatureHobbyByArt;
  // Prefer URL slug so parent category (caricature-cartoon) does not replace hobby-caricature.
  const routeSlug = isHobbyCaptureFlow
    ? resolveHobbyCaricatureRouteSlug(slug)
    : slug || stationData?.type || "";

  const isGroupPhotoFlow =
    isHobbyCaptureFlow &&
    (hobbyGroupPhotoFlow ||
      Boolean((location.state as { groupPhotoFlow?: boolean } | null)?.groupPhotoFlow));

  const isHobbyMulti =
    isHobbyCaptureFlow && !isGroupPhotoFlow && (hobbyPeopleCount ?? 0) > 1;

  const nextPersonIndex = getCurrentCapturePersonNumber();
  const isSecondPersonOrLater = isHobbyMulti && nextPersonIndex >= 2;

  const handleBack = () => {
    if (isSecondPersonOrLater) {
      setShowBackConfirmModal(true);
      return;
    }
    if (aiSelfiePostPaymentHobby || caricatureHobbyByArt || isGroupPhotoFlow) {
      navigate(`/${routeSlug || "hobby-caricature"}/select-people`);
      return;
    }
    navigate(-1);
  };

  useEffect(() => {
    if (!isHobbyCaptureFlow) return;
    if (isGroupPhotoFlow) {
      dispatch(setHobbyGroupPhotoFlow(true));
      return;
    }
    if (hobbyPeopleCount === null) {
      dispatch(initHobbyPeopleSession(1));
    }
  }, [
    stationData?.type,
    hobbyPeopleCount,
    dispatch,
    isHobbyCaptureFlow,
    isGroupPhotoFlow,
  ]);

  useEffect(() => {
    if (!isHobbyCaptureFlow || !allHobbyPersonsCompleted()) return;
    navigate(`/${routeSlug || "hobby-caricature"}/created-person-hobby`, {
      replace: true,
      state: {
        generationResult: { imageUrls: hobbyPersonCompletedResults },
        isFinalCombine: true,
      },
    });
  }, [
    isHobbyCaptureFlow,
    hobbyPersonCompletedResults,
    navigate,
    routeSlug,
  ]);

  const goToExtractPerson = (imageUrl: string) => {
    setPhotoModal(false);
    dispatch(setHobbyGroupPhotoFlow(true));
    navigate(`/${routeSlug || "hobby-caricature"}/extract-person`, {
      state: {
        groupImageUrl: imageUrl,
        groupPhotoFlow: true,
        stationData,
      },
    });
  };

  return (
    <>
      <StationThemeLayout
        className="chooseOptionSection p-0"
        station={stationData}
        showStationLogo
        onBack={handleBack}
      >
        <div className="h-100 chooseCard">
          <div className="chooseContainer">
            <div
              className="heading"
              style={isGroupPhotoFlow ? { marginBottom: 24 } : undefined}
            >
              Choose an Option
            </div>
            {isGroupPhotoFlow ? (
              <div
                className="description text-center"
                style={{ fontWeight: 500, fontSize: "20px", color: "#fff", marginBottom: 32 }}
              >
                Take or upload one photo with everyone in it.
                <br />
                We&apos;ll extract each person automatically.
              </div>
            ) : null}
            {isHobbyMulti ? (
              <div className="description text-center mb-4" style={{ fontWeight: 600, fontSize: "22px", color: "#fff" }}>
                Person {nextPersonIndex} of {hobbyPeopleCount}
              </div>
            ) : null}
            <div className="optionSection">
              <div className="singleOption" onClick={() => setPhotoModal(true)}>
                <img src={Photo} alt="Take a photo" className="optionIMG" />
                <div className="optionTxt">Take a photo</div>
              </div>
              <div
                className="singleOption"
                onClick={() =>
                  navigate(`/${routeSlug || "hobby-caricature"}/upload-image-qr-code`, {
                    state: isGroupPhotoFlow ? { groupPhotoFlow: true } : undefined,
                  })
                }
              >
                <img src={Upload} alt="Upload a photo" className="optionIMG" />
                <div className="optionTxt">Upload a photo</div>
              </div>
            </div>
          </div>
        </div>
      </StationThemeLayout>

      {photoModal &&
        <TakePhotoModal
          show={photoModal}
          onHide={() => setPhotoModal(false)}
          onPhotoReady={isGroupPhotoFlow ? goToExtractPerson : undefined}
          skipBackgroundRemoval={isGroupPhotoFlow}
        />
      }

      {showBackConfirmModal && (
        <SignOutConfirmModal
          show={showBackConfirmModal}
          onHide={() => setShowBackConfirmModal(false)}
          onConfirm={() => {
            setShowBackConfirmModal(false);
            navigate("/");
          }}
          message="Are you sure you want to go back to the home screen?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}
    </>
  );
};

export default SelectMediaLibrary;
